/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  Modal,
  TextField,
  FormHelperText,
  Theme,
  SxProps,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RootState, useAppDispatch } from "@store/store";
import { useValidatePhoneCodeMutation, useRequestPhoneCodeMutation } from "@api/onboarding";
import { ProgressBar } from "@components/ProgressBar";
import { formatTime, formatPhoneNumber } from "@helpers/funciones";
import "@styles/stylesRegistro.css";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import ModalBuroNip from "@components/ModalBuroNip";
import { CustomSwitch } from "@components/CustomSwitch";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { setBuroNip, setBuroNipLastCodeRequest, setShareholderUpdatePhone } from "@store/slices/registerSlice";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import dayjs from "dayjs";
import CustomCountryCode from "@components/CustomContryCode/CustomContryCode";
import { usePatchPersonMutation } from "@api/personasApi";

const TIME_LIMIT = 120;
const ONLY_NUMBERS_REGEX = /^[0-9]+$/;
const SCREEN_STATUS = "CSABA";

const CustomTextField: SxProps<Theme> = (theme) => ({
  "& input": {
    textAlign: "center",
    fontSize: "25px",
    fontWeight: 400,
    width: "40px",
    height: "60px",
  },
  margin: "8px",
  backgroundColor: theme?.palette?.components?.phoneInput?.main || theme?.palette?.primary?.base,
  borderRadius: "10px",
  "& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
  },
  "& input[disabled]": {
    cursor: "not-allowed",
  },
});

const ConsultaBuroNip = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const resendEffectRanRef = useRef(false);

  const inputRef_1 = useRef<HTMLInputElement>(null);
  const inputRef_2 = useRef<HTMLInputElement>(null);
  const inputRef_3 = useRef<HTMLInputElement>(null);
  const inputRef_4 = useRef<HTMLInputElement>(null);

  const shareholder = useSelector((state: RootState) => state.register.shareholder);
  const buroNip = useSelector((state: RootState) => state.register.buroNip);

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [patchPerson] = usePatchPersonMutation();

  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);

  const [phoneData, setPhoneData] = useState({
    code_phone_number: shareholder.code_phone_number,
    phone_number: shareholder.phone_number,
  });

  const formik = useFormik({
    initialValues: {
      code_1: "",
      code_2: "",
      code_3: "",
      code_4: "",
      switch: buroNip?.termsAccepted || false,
    },
    validationSchema: yup.object().shape({
      code_1: yup.string(),
      code_2: yup.string(),
      code_3: yup.string(),
      code_4: yup.string(),
      switch: yup.boolean().oneOf([true], "Debes aceptar los términos y condiciones"),
    }),
    onSubmit: async () => {
      setOpenModalConfirm(true);
    },
  });

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasteData = e.clipboardData.getData("text").trim().slice(0, 4);

    if (pasteData.length === 4) {
      formik.setFieldValue("code_1", pasteData[0]);
      formik.setFieldValue("code_2", pasteData[1]);
      formik.setFieldValue("code_3", pasteData[2]);
      formik.setFieldValue("code_4", pasteData[3]);

      inputRef_4.current?.focus();
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);

      if (buroNip.isValid) {
        return;
      }

      if (isRequestingCode) {
        return;
      }

      const lastCodeRequest = buroNip.last_code_request ? dayjs(buroNip.last_code_request) : null;
      const currentTime = dayjs();
      const secondsDiff = lastCodeRequest ? currentTime.diff(lastCodeRequest, "seconds") : null;

      if (secondsDiff && secondsDiff < TIME_LIMIT) {
        return;
      }

      await requestPhoneCode({
        country_code: shareholder.code_phone_number,
        phone_number: shareholder.phone_number,
      }).unwrap();

      dispatch(setBuroNipLastCodeRequest({ last_code_request: currentTime.toISOString() }));
      setSecondsRemaining(TIME_LIMIT);
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 429) {
        handleOpen();
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    try {
      if (buroNip?.isValid) {
        setErrorMessage(null);
        return;
      }

      const fullCode = [
        formik.values.code_1 || "",
        formik.values.code_2 || "",
        formik.values.code_3 || "",
        formik.values.code_4 || "",
      ].join("");

      if (fullCode.length < 4) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      await validatePhoneCode({
        country_code: shareholder.code_phone_number,
        phone_number: shareholder.phone_number,
        code: fullCode,
      }).unwrap();

      dispatch(
        setBuroNip({
          code: fullCode,
          isValid: true,
          termsAccepted: false,
        }),
      );
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 404 || api_error?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        formik.setFieldValue("code_1", "");
        formik.setFieldValue("code_2", "");
        formik.setFieldValue("code_3", "");
        formik.setFieldValue("code_4", "");
        return;
      }

      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      Sentry.captureException(api_error, {
        extra: {
          component: "ValidacionTelefono",
          values: formik.values,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (secondsRemaining > 0) {
        setSecondsRemaining(secondsRemaining - 1);
      } else {
        clearInterval(intervalId);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [secondsRemaining]);

  useEffect(() => {
    validateCode();
  }, [formik.values.code_1, formik.values.code_2, formik.values.code_3, formik.values.code_4]);

  useEffect(() => {
    dispatch(
      setBuroNip({
        code: buroNip?.code,
        isValid: buroNip.isValid,
        termsAccepted: formik.values.switch,
      }),
    );
  }, [formik.values.switch]);

  useEffect(() => {
    if (!buroNip?.isValid && !resendEffectRanRef.current) {
      resendEffectRanRef.current = true;
      handleResendCode();
    }

    if (buroNip?.isValid) {
      formik.setFieldValue("code_1", buroNip.code[0]);
      formik.setFieldValue("code_2", buroNip.code[1]);
      formik.setFieldValue("code_3", buroNip.code[2]);
      formik.setFieldValue("code_4", buroNip.code[3]);
    }
  }, [buroNip?.isValid]);

  useEffect(() => {
    if (buroNip?.isValid) return;
    resendEffectRanRef.current = false;
  }, [shareholder.code_phone_number, shareholder.phone_number, buroNip?.isValid]);

  const closeModalEdit = () => {
    setOpenEdit(false);
  };

  const handleSavePhone = async () => {
    try {
      setIsLoading(true);
      const payload = {
        code_phone_number: phoneData.code_phone_number,
        phone_number: phoneData.phone_number,
      };
      await patchPerson({
        personId: shareholder.id,
        body: payload,
      }).unwrap();

      await requestPhoneCode({
        country_code: phoneData.code_phone_number,
        phone_number: phoneData.phone_number,
      }).unwrap();

      dispatch(setShareholderUpdatePhone(payload));
      setOpenEdit(false);
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 429) {
        handleOpen();
        return;
      }
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      Sentry.captureException(api_error, {
        extra: {
          component: "ConsultaBuroNip - Editar teléfono",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.consulta_buro_nip?.titulo || "Validación para consulta buró"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3} textAlign={"center"}>
            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
              Ingresa el código que enviamos vía whatsapp al número
            </Typography>
          </Box>

          <Typography
            variant={isMobileOrTablet ? "h5" : "h4"}
            mt={isMobileOrTablet ? 2 : 3}
            fontWeight={600}
            color={theme?.palette?.primary?.main}
          >
            ({shareholder.code_phone_number}) {formatPhoneNumber(shareholder.phone_number)}
          </Typography>

          {!buroNip?.isValid && (
            <Typography
              mt={1}
              variant={isMobileOrTablet ? "body2" : "body2"}
              component={"a"}
              onClick={() => setOpenEdit(true)}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              color={theme?.palette?.primary?.main}
            >
              ¿No es tu número? Editalo aquí
            </Typography>
          )}

          <Typography
            mt={2}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={300}
            color={theme?.palette?.primary?.dark}
          >
            Código {buroNip?.isValid ? "verificado" : "no verificado"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 1 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box display="flex" flexDirection="row" justifyContent="center">
                <TextField
                  type="text"
                  name="code_1"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                      inputRef_2.current?.focus();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace") {
                      formik.setFieldValue("code_1", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_1 && Boolean(formik.errors.code_1)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_1}
                  disabled={isLoading || formik.isSubmitting || buroNip?.isValid}
                  value={formik.values.code_1}
                />

                <TextField
                  type="text"
                  name="code_2"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                      inputRef_3.current?.focus();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace" && !formik.values.code_2) {
                      inputRef_1.current?.focus();
                    } else if (e.code === "Backspace") {
                      formik.setFieldValue("code_2", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_2 && Boolean(formik.errors.code_2)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_2}
                  disabled={isLoading || formik.isSubmitting || buroNip?.isValid}
                  value={formik.values.code_2}
                />

                <TextField
                  type="text"
                  name="code_3"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                      inputRef_4.current?.focus();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace" && !formik.values.code_3) {
                      inputRef_2.current?.focus();
                    } else if (e.code === "Backspace") {
                      formik.setFieldValue("code_3", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_3 && Boolean(formik.errors.code_3)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_3}
                  disabled={isLoading || formik.isSubmitting || buroNip?.isValid}
                  value={formik.values.code_3}
                />

                <TextField
                  type="text"
                  name="code_4"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace" && !formik.values.code_4) {
                      inputRef_3.current?.focus();
                    } else if (e.code === "Backspace") {
                      formik.setFieldValue("code_4", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_4 && Boolean(formik.errors.code_4)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_4}
                  disabled={isLoading || formik.isSubmitting || buroNip?.isValid}
                  value={formik.values.code_4}
                />
              </Box>

              {errorMessage && <FormHelperText sx={{ mt: isMobileOrTablet ? 2 : 1 }}>{errorMessage}</FormHelperText>}

              {!buroNip?.isValid && (
                <>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    mt={isMobileOrTablet ? 2 : 3}
                    textAlign="center"
                    fontWeight={600}
                    color={theme?.palette?.primary?.main}
                  >
                    {formatTime(secondsRemaining)} min
                  </Typography>

                  {secondsRemaining === 0 && (
                    <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        component={"a"}
                        onClick={handleResendCode}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Reenviar código
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Box display={"flex"} justifyContent={"center"} mt={2} textAlign="center">
                <FormControlLabel
                  control={<CustomSwitch />}
                  label={
                    <Box sx={{ paddingTop: "10px" }}>
                      <Typography variant="body2" color={theme?.palette?.primary?.dark}>
                        <CustomTextCiecBuro type="nipBuro" />
                      </Typography>
                    </Box>
                  }
                  name="switch"
                  checked={formik.values.switch}
                  onChange={formik.handleChange}
                  disabled={isLoading || formik.isSubmitting || !buroNip?.isValid}
                />
              </Box>

              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={isLoading || !buroNip?.isValid || formik.isSubmitting || !formik.isValid}
                >
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography fontWeight={"600"} gutterBottom color={theme?.palette?.primary?.dark} fontSize={"20px"}>
            Excedió el número de intentos permitidos para ese número
          </Typography>
          <Typography fontSize={"16px"} sx={{ mt: 2, mb: 2 }} color={theme?.palette?.primary?.dark}>
            intenta más tarde o contacta a un asesor.
          </Typography>
          <Button variant="default" color="primary" onClick={handleClose}>
            Cerrar
          </Button>
        </Box>
      </Modal>

      <ModalBuroNip screenStatus={SCREEN_STATUS} open={openModalConfirm} onClose={() => setOpenModalConfirm(false)} />

      <Dialog
        open={openEdit}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            closeModalEdit();
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: theme?.palette?.primary?.dark, position: "relative" }}>
          Editar número de teléfono
          <IconButton
            aria-label="Cerrar"
            onClick={closeModalEdit}
            sx={{ position: "absolute", right: 8, top: 8, color: theme?.palette?.primary?.dark }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={5}>
              <CustomCountryCode
                label="Código"
                name="code_phone_number"
                value={phoneData.code_phone_number}
                onChange={(ev) => {
                  if (buroNip.isValid) return;
                  setPhoneData({ ...phoneData, code_phone_number: ev.target.value });
                }}
                disabled={buroNip.isValid}
              />
            </Grid>
            <Grid item xs={7}>
              <FormControl fullWidth>
                <TextField
                  id="phone_number"
                  name="phone_number"
                  label="Teléfono"
                  fullWidth
                  autoComplete="off"
                  onChange={(ev) => {
                    if (buroNip.isValid) return;
                    setPhoneData({ ...phoneData, phone_number: ev.target.value });
                  }}
                  value={phoneData.phone_number}
                  inputProps={{ maxLength: 10 }}
                  disabled={buroNip.isValid}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Button
                variant="default"
                color="primary"
                sx={{ width: "100%" }}
                disabled={buroNip.isValid || isLoading}
                onClick={handleSavePhone}
              >
                Editar
              </Button>
            </Grid>

            <Grid item xs={6}>
              <Button
                variant="blue_outlined"
                color="primary"
                onClick={closeModalEdit}
                sx={{ width: "100%" }}
                disabled={buroNip.isValid || isLoading}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default ConsultaBuroNip;
